// src/hooks/useModulePermissions.ts
import { useMemo, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  EnhancedRoleDefinition,
  NavigationModule,
  AccessLevel,
  SYSTEM_ROLES,
  getModulePermission,
  hasModuleAccess,
  getUserAccessibleModules
} from '../types/modulePermissions';
import { supabase } from '../supabase/config';

export const useModulePermissions = () => {
  const { user } = useAuth();
  const [customRoles, setCustomRoles] = useState<EnhancedRoleDefinition[]>([]);
  const [loading, setLoading] = useState(true);

  // Load custom roles from database
  useEffect(() => {
    loadCustomRoles();
  }, []);

  const loadCustomRoles = async () => {
    try {
      const { data, error } = await supabase
        .from('custom_roles')
        .select('*');
        
      if (error) {
        console.error('Error loading custom roles:', error);
        setLoading(false);
        return;
      }
      
      const roles: EnhancedRoleDefinition[] = (data || []).map(item => ({
        id: item.id,
        name: item.name || '',
        description: item.description || '',
        level: item.level || 5,
        isCustom: true,
        isSystemAdmin: item.is_system_admin || false,
        modulePermissions: item.permissions || [],
        createdAt: item.created_at ? new Date(item.created_at) : undefined,
        updatedAt: item.updated_at ? new Date(item.updated_at) : undefined
      }));
      
      setCustomRoles(roles);
    } catch (error) {
      console.error('Error loading custom roles:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get current user's role definition
  const currentRole = useMemo((): EnhancedRoleDefinition | null => {
    console.log('useModulePermissions: Calculating currentRole for user:', {
      userName: user?.name,
      userRole: user?.role,
      userEmail: user?.email,
      hasUser: !!user
    });

    if (!user?.role) {
      console.log('useModulePermissions: No user or role, returning null');
      return null;
    }

    // Check system roles first
    const systemRole = SYSTEM_ROLES.find(role =>
      role.id === user.role ||
      // Handle legacy role names
      (user.role === 'admin' && role.id === 'system_admin')
    );

    console.log('useModulePermissions: System role match attempt:', {
      userRole: user.role,
      foundSystemRole: systemRole?.id,
      isSystemAdmin: systemRole?.isSystemAdmin
    });

    if (systemRole) {
      console.log('useModulePermissions: Returning system role:', systemRole.id);
      return systemRole;
    }

    // Check custom roles
    const customRole = customRoles.find(role => role.id === user.role);
    if (customRole) {
      console.log('useModulePermissions: Returning custom role:', customRole.id);
      return customRole;
    }

    // Fallback to teacher role if no match found
    console.log('useModulePermissions: No role match found, falling back to teacher for role:', user.role);
    return SYSTEM_ROLES.find(role => role.id === 'teacher') || null;
  }, [user?.role, user?.name, user?.email, customRoles]);

  // Check if user has access to a specific module
  const hasAccess = useMemo(() => {
    return (module: NavigationModule, requiredLevel: AccessLevel = 'read'): boolean => {
      if (!currentRole) return false;
      return hasModuleAccess(currentRole, module, requiredLevel);
    };
  }, [currentRole]);

  // Get user's access level for a specific module
  const getAccess = useMemo(() => {
    return (module: NavigationModule): AccessLevel => {
      if (!currentRole) return 'none';
      return getModulePermission(currentRole, module);
    };
  }, [currentRole]);

  // Get all modules the user can access
  const accessibleModules = useMemo((): NavigationModule[] => {
    if (!currentRole) return [];
    return getUserAccessibleModules(currentRole);
  }, [currentRole]);

  // Check if user is system admin
  const isSystemAdmin = useMemo((): boolean => {
    return currentRole?.isSystemAdmin || false;
  }, [currentRole]);

  // Get user's role level
  const roleLevel = useMemo((): number => {
    return currentRole?.level || 0;
  }, [currentRole]);

  // Check if user can manage another role (by level)
  const canManageRole = useMemo(() => {
    return (targetRoleLevel: number): boolean => {
      if (isSystemAdmin) return true;
      return roleLevel > targetRoleLevel;
    };
  }, [isSystemAdmin, roleLevel]);

  return {
    currentRole,
    hasAccess,
    getAccess,
    accessibleModules,
    isSystemAdmin,
    roleLevel,
    canManageRole,
    loading,
    // Utility functions
    canRead: (module: NavigationModule) => hasAccess(module, 'read'),
    canWrite: (module: NavigationModule) => hasAccess(module, 'write'),
    // Legacy compatibility
    hasPermission: hasAccess, // For backward compatibility
  };
};

export default useModulePermissions;